import { useEffect, useRef, useState, ReactNode } from "react";

interface LazySectionProps {
  children: ReactNode;
  /** Root margin for IntersectionObserver (default: 200px to start loading early) */
  rootMargin?: string;
  /** Minimum height placeholder to prevent CLS */
  minHeight?: string;
  className?: string;
}

/**
 * Lightweight viewport-triggered lazy loader.
 * Renders children only when the section is about to enter the viewport.
 * Uses native IntersectionObserver — zero JS framework overhead.
 */
const LazySection = ({
  children,
  rootMargin = "200px",
  minHeight = "100px",
  className,
}: LazySectionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} className={className} style={visible ? undefined : { minHeight }}>
      {visible ? children : null}
    </div>
  );
};

export default LazySection;
